import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { ALL_MERGE_RULES, MERGE_RULES } from '@gitbook/app-merge-rules';
import { Checkbox, Description, Label } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { MergeRuleCustomEditor } from './MergeRuleCustomEditor';
import { MergeRuleUsersPicker } from './MergeRuleUsersPicker';

/**
 * Editor for the presets merge rule configuration.
 */
export function MergeRulesEditor(props: {
    configuration: api.MergeRulesConfigurationRules;
    onChange: (configuration: api.MergeRulesConfigurationRules) => void;
}) {
    const { configuration, onChange } = props;

    return (
        <div className="flex flex-col gap-5">
            {ALL_MERGE_RULES.map((rule) => {
                return (
                    <MergeRuleCheckableEditor
                        key={rule}
                        configuration={configuration}
                        rule={rule}
                        onChange={onChange}
                    />
                );
            })}
        </div>
    );
}

function MergeRuleCheckableEditor(props: {
    configuration: api.MergeRulesConfigurationRules;
    rule: api.MergeRule['rule'];
    onChange: (configuration: api.MergeRulesConfigurationRules) => void;
}) {
    const { configuration, rule, onChange } = props;

    const id = React.useId();
    const definition = MERGE_RULES[rule];
    const mergeRule = configuration.rules.find((r) => r.rule === rule);

    const onChangeRule = (rule: api.MergeRule) => {
        onChange({
            ...configuration,
            rules: configuration.rules.map((r) => (r.rule === rule.rule ? rule : r)),
        });
    };

    return (
        <div key={rule} className={clsx('flex flex-row gap-4')}>
            <div>
                <Checkbox
                    id={id}
                    value={!!mergeRule}
                    onChange={(selected) => {
                        if (!selected) {
                            onChange({
                                ...configuration,
                                rules: configuration.rules.filter((r) => r.rule !== rule),
                            });
                        } else {
                            onChange({
                                ...configuration,
                                rules: [...configuration.rules, getRuleInitialValue(rule)],
                            });
                        }
                    }}
                />
            </div>
            <div className="flex flex-1 flex-col gap-2">
                <label htmlFor={id} className="flex flex-col">
                    <Label htmlFor={id}>{definition.title}</Label>
                    <Description>{definition.description}</Description>
                </label>
                {mergeRule?.rule === 'require_specific_reviewers' ||
                mergeRule?.rule === 'require_one_of_specific_reviewers' ||
                mergeRule?.rule === 'allow_bypass' ? (
                    <MergeRuleUsersPicker mergeRule={mergeRule} onChange={onChangeRule} />
                ) : null}
                {mergeRule?.rule === 'custom' ? (
                    <MergeRuleCustomEditor mergeRule={mergeRule} onChange={onChangeRule} />
                ) : null}
            </div>
        </div>
    );
}

function getRuleInitialValue(rule: api.MergeRule['rule']): api.MergeRule {
    switch (rule) {
        case 'allow_bypass':
        case 'require_specific_reviewers':
        case 'require_one_of_specific_reviewers':
            return { rule, users: [] };
        case 'require_at_least_one_review':
        case 'require_all_reviews_approved':
        case 'require_agent_review':
        case 'require_up_to_date_change_request':
        case 'require_change_request_subject':
        case 'require_change_request_description':
            return { rule };
        case 'custom':
            return { rule, expression: '' };
        default:
            assertNever(rule);
    }
}
